/**
 * Calendar days picked in the browser, turned into the UTC instants the API expects (HS-1548, A3).
 *
 * `DateField` hands back a bare `yyyy-MM-dd` in the browser's own calendar. A request that
 * says "until 12 March" means the whole of that day where the administrator sits, so an upper
 * bound is the last millisecond of the local day and a lower bound its first. Both are sent as
 * UTC instants (`toISOString`). If the product ever wants another convention, only this module
 * changes.
 *
 * Unlike `endOfDayUtc` in `listQueryParams.ts` — the filter builder's older rule, which closes
 * the day in UTC — these read the local calendar, because the decision and the requests list
 * were specified that way (sweep Q1, "end of day, local").
 */

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const ISO_PAD_LENGTH = 2;
const LAST_HOUR = 23;
const LAST_MINUTE = 59;
const LAST_SECOND = 59;
const LAST_MILLISECOND = 999;

/** `yyyy-MM-dd` → the local calendar day it names, or `undefined` for anything else. */
function parseLocalDay(
  isoDate: string,
): { year: number; monthIndex: number; day: number } | undefined {
  const match = ISO_DATE.exec(isoDate);
  if (match === null) return undefined;
  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  const day = Number(match[3]);
  // `new Date(2026, 1, 31)` silently rolls over into March; refuse a day the month does not have.
  const probe = new Date(year, monthIndex, day);
  if (probe.getFullYear() !== year || probe.getMonth() !== monthIndex || probe.getDate() !== day) {
    return undefined;
  }
  return { year, monthIndex, day };
}

/** The last millisecond of a local calendar day, as a UTC instant. */
export function toEndOfDayInstant(isoDate: string): string | undefined {
  const parsed = parseLocalDay(isoDate);
  if (parsed == null) return undefined;
  return new Date(
    parsed.year,
    parsed.monthIndex,
    parsed.day,
    LAST_HOUR,
    LAST_MINUTE,
    LAST_SECOND,
    LAST_MILLISECOND,
  ).toISOString();
}

/** The first millisecond of a local calendar day, as a UTC instant. */
export function toStartOfDayInstant(isoDate: string): string | undefined {
  const parsed = parseLocalDay(isoDate);
  if (parsed == null) return undefined;
  return new Date(parsed.year, parsed.monthIndex, parsed.day).toISOString();
}

/** A `Date` → the `yyyy-MM-dd` of its local calendar day (never `toISOString`, which reads UTC). */
export function toLocalIsoDate(date: Date): string {
  const pad = (value: number) => String(value).padStart(ISO_PAD_LENGTH, '0');
  return `${String(date.getFullYear())}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** An instant from the API → the local calendar day it falls on, or `undefined` if unparseable. */
export function instantToLocalIsoDate(instant: string): string | undefined {
  const date = new Date(instant);
  if (Number.isNaN(date.getTime())) return undefined;
  return toLocalIsoDate(date);
}

/** Midnight at the start of today, local time — the earliest day a future-only picker offers. */
export function startOfToday(now: Date = new Date()): Date {
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}
